import { cn } from "@RHCommerceDev/utils/cn";
import React, { useEffect, useState } from "react";
import { Typography } from "@RHCommerceDev/unstyled-ui-components";
import memoize from "utils/memoize";
import he from "he";
import { getScrollbarWidth } from "utils/scrollLock";

interface CatalogNavigationProps {
  categories: Array<NavigationElement>;
  selectedCategoryIndex: number;
  isFrenchMontreal: boolean;
  isConcierge: boolean;
  onCategoryLinkClick: (
    event: React.MouseEvent,
    category: NavigationElement
  ) => void;
  onCategoryDropdownToggle: (categoryIndex: number) => void;
}

const CatalogNavigation = ({
  categories,
  selectedCategoryIndex,
  isFrenchMontreal,
  isConcierge,
  onCategoryLinkClick,
  onCategoryDropdownToggle
}: CatalogNavigationProps) => {
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  useEffect(() => {
    const update = () => setScrollbarWidth(getScrollbarWidth());
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return (
    <>
      {/* Navigation is hidden below md */}
      <div
        className={cn(
          "hidden w-full flex-nowrap justify-between p-0 px-10 md:flex xl:px-20",
          {
            "pb-4": isConcierge
          }
        )}
        style={{
          marginRight:
            selectedCategoryIndex >= 0 ? `${scrollbarWidth}px` : undefined
        }}
        onClick={e => {
          //this stop dropdown to close when clicked
          e.stopPropagation();
        }}
      >
        {categories?.map((category, index) => {
          return !!category?.childCategories?.length ? (
            <button
              key={index}
              onClick={() => {
                onCategoryDropdownToggle(index);
              }}
              className="p-0 text-inherit"
            >
              <Typography
                variant="default"
                fontFamily="rhc"
                fontWeight="thin"
                data-analytics-rootcat={category.displayName}
                className={cn(
                  "text-left text-[13px] uppercase leading-[1.5em] tracking-[0.05em] lg:text-base",
                  {
                    "opacity-30":
                      selectedCategoryIndex !== -1 &&
                      index !== selectedCategoryIndex,
                    "tracking-[5%] md:text-[11px] lg:text-[12.5px] xl:text-[14px]":
                      isFrenchMontreal,
                    "font-semibold": index === selectedCategoryIndex
                  }
                )}
                dangerouslySetInnerHTML={{
                  __html: he.decode(category?.displayName ?? "")
                }}
              />
            </button>
          ) : (
            <a
              key={index}
              className="flex items-end text-inherit no-underline"
              onClick={e => {
                e.preventDefault();
                onCategoryLinkClick(e, category);
              }}
            >
              <Typography
                variant="default"
                fontFamily="rhc"
                fontWeight="thin"
                className={cn(
                  "text-left text-[13px] uppercase leading-[1.5em] tracking-[0.05em] lg:text-base",
                  {
                    "opacity-30":
                      selectedCategoryIndex !== -1 &&
                      index !== selectedCategoryIndex,
                    "tracking-[5%] md:text-[11px] lg:text-[12.5px] xl:text-[14px]":
                      isFrenchMontreal
                  }
                )}
                dangerouslySetInnerHTML={{
                  __html: he.decode(category?.displayName ?? "")
                }}
              />
            </a>
          );
        })}
      </div>
    </>
  );
};

export default memoize(CatalogNavigation);
